import { MapPin } from "lucide-react";
import { Reveal, PageHero, SectionHead } from "../components/Reveal";
import NetworkMap from "../components/NetworkMap";
import { NETWORK, IMAGES } from "../data/site";
import useSeo from "../components/Seo";

// Bottom statistics are computed from the verified network data in
// src/data/site.js — never hardcoded.
const pad2 = (n) => String(n).padStart(2, "0");

function networkStats() {
  const operational = NETWORK.locations.filter((l) => l.status === "operational");
  const states = new Set([NETWORK.hq.state, ...operational.map((l) => l.state)]);
  return [
    [pad2(operational.length), "Active Logistics Corridors"],
    [pad2(states.size), "States & UTs Served"],
    ["01", "HQ — Bengaluru"],
  ];
}

export default function Network() {
  useSeo(
    "Logistics Network — Pan-India Coverage",
    "India logistics network map of The Royal Mahaveer Roadlines Pvt Ltd — pan-India road transportation headquartered in Bengaluru with active corridors to Delhi NCR, Mumbai, Pune, Ahmedabad, Jaipur, Lucknow, Hyderabad, Chennai, Kochi, Kolkata and Guwahati."
  );
  const stats = networkStats();
  return (
    <>
      <PageHero
        overline="Our Transportation Network"
        lines={[<>All over India,</>, <><em className="italic text-gold-light">we reach</em></>]}
        desc="From our registered headquarters in Bengaluru, Karnataka, our logistics network covers every region of the country — North, South, East and West. Select any location on the map to see its corridor coverage."
      />

      <section className="py-24 sm:py-32" data-testid="network-map-section">
        <div className="mx-auto grid max-w-7xl items-start gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-7">
            <NetworkMap />

            <div className="mt-8 grid grid-cols-3 divide-x divide-gold/15 border border-gold/15 bg-surface" data-testid="network-stats-strip">
              {stats.map(([v, l]) => (
                <div key={l} className="px-3 py-7 text-center">
                  <p data-testid={`network-stat-${v}`} className="font-heading text-2xl text-foreground sm:text-4xl">{v}</p>
                  <p className="mt-2 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground sm:text-[10px]">{l}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[9px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/60" data-testid="network-stats-note">
              Corridors to metro destinations · coverage coordinated across every state and union territory
            </p>
          </Reveal>

          <div className="lg:col-span-5">
            <SectionHead overline="Coverage" title="One network. All of India." />
            <Reveal delay={0.1}>
              <div className="mt-12 border border-gold/40 bg-gold/5 p-8" data-testid="network-panindia-card">
                <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-gold">Pan-India Service — Confirmed</p>
                <p className="mt-4 text-sm leading-relaxed text-foreground/90 sm:text-base">{NETWORK.coverage}</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 grid grid-cols-2 gap-px border border-gold/15 bg-gold/15" data-testid="network-hub-list">
                {[{ id: NETWORK.hq.id, label: NETWORK.hq.label, status: "hq" }, ...NETWORK.locations].map((loc, idx) => (
                  <div
                    key={loc.id}
                    data-testid={`network-hub-${loc.id}`}
                    className={`group flex items-center gap-2.5 px-4 py-4 transition-colors duration-300 ${
                      idx === 0 ? "bg-gold/10 hover:bg-gold/15" : "bg-ink hover:bg-smoke"
                    }`}
                  >
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-gold" />
                    <span className={`text-xs font-medium transition-colors group-hover:text-gold-light ${idx === 0 ? "text-gold-light" : "text-foreground/85"}`}>
                      {loc.label}
                      {idx === 0 && <span className="ml-2 text-[9px] font-semibold uppercase tracking-[0.18em] text-gold">HQ</span>}
                      {loc.status === "planned" && <span className="ml-2 text-[9px] font-semibold uppercase tracking-[0.18em] text-amber-400/80">Soon</span>}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-9 max-w-xl text-sm leading-[1.9] text-muted-foreground">
                Active corridors are drawn on the map from our Bengaluru headquarters. For any
                origin–destination pair across India, share your requirement and we will confirm
                vehicle allocation, timeline and a clear plan.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-gold/10 py-24" data-testid="network-cta-section">
        <img src={IMAGES.network} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" style={{ filter: "grayscale(0.6) brightness(0.5) sepia(0.3)" }} loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink" />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl text-foreground sm:text-4xl">Have a route in mind?</h2>
            <p className="mx-auto mt-5 max-w-lg text-sm leading-relaxed text-muted-foreground">
              Share your pickup and delivery points — we will confirm feasibility and a clear plan.
            </p>
            <a href="/quote" className="btn-solid mt-9 inline-flex" data-testid="network-quote-cta">Request a Quote</a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
